import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import {
  ArrowUpRight,
  CalendarDays,
  Download,
  Receipt,
  Sparkles,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const subscription = {
  tier: "Core",
  startDate: "2025-09-09",
  monthsRemaining: 6,
  assessmentsThisMonth: 2,
  monthsActive: 6,
  billing: {
    monthly: "$750",
    quarterly: "$2,250",
  },
  invoices: [
    {
      id: "INV-2026-Q1",
      date: "Mar 1, 2026",
      amount: "$2,250",
      status: "upcoming",
    },
    { id: "INV-2025-Q4", date: "Dec 1, 2025", amount: "$2,250", status: "paid" },
    { id: "INV-2025-Q3", date: "Sep 1, 2025", amount: "$2,250", status: "paid" },
  ],
};

const tierDetails = {
  Core: {
    price: "$750/mo",
    quarterly: "$2,250/qtr",
    assessments: "1 assessment/month (3/quarter)",
  },
  Pro: {
    price: "$500/mo",
    quarterly: "$4,500/qtr",
    assessments: "3 assessments/month",
  },
  Enterprise: {
    price: "Custom",
    quarterly: "Custom",
    assessments: "Unlimited assessments",
  },
};

const getUpgradeInfo = (tier, assessmentsThisMonth, monthsActive) => {
  if (tier === "Core" && monthsActive >= 6 && assessmentsThisMonth > 1) {
    return {
      show: true,
      nextTier: "Pro",
      messageKey: "settings.upgradeCopy",
    };
  }
  if (tier === "Pro" && assessmentsThisMonth > 3) {
    return {
      show: true,
      nextTier: "Enterprise",
      messageKey: "subscription.enterpriseUpgradeCopy",
    };
  }
  return { show: false, nextTier: tier, messageKey: "" };
};

const SubscriptionCard = () => {
  const { t } = useLanguage();
  const { tier, monthsRemaining, assessmentsThisMonth, monthsActive, invoices } =
    subscription;
  const upgrade = getUpgradeInfo(tier, assessmentsThisMonth, monthsActive);
  const details = tierDetails[tier];

  return (
    <Card className="lg:col-span-2 shadow-[var(--card-shadow)]">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg font-display">
          {t("Subscription")}
        </CardTitle>
        <Badge variant="secondary" className="text-xs font-semibold">
          {tier}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Plan & Remaining */}
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
            <CalendarDays className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">
              {monthsRemaining} {t("months remaining")}
            </p>
            <p className="text-xs text-muted-foreground">
              {t(details.assessments)} · {t("Billed quarterly at")}{" "}
              {details.quarterly === "Custom"
                ? t("Custom")
                : details.quarterly}
            </p>
          </div>
        </div>

        <Separator />

        {/* Quarterly Invoices */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Receipt className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              {t("Quarterly Invoices")}
            </span>
          </div>
          <div className="space-y-2">
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <div>
                    <p className="text-sm font-medium">{inv.id}</p>
                    <p className="text-xs text-muted-foreground">{inv.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">{inv.amount}</span>
                  <Badge
                    variant={inv.status === "paid" ? "secondary" : "default"}
                    className="text-[10px]"
                  >
                    {inv.status === "paid" ? t("Paid") : t("Upcoming")}
                  </Badge>
                  {inv.status === "paid" && (
                    <Button variant="ghost" size="icon" className="h-7 w-7">
                      <Download className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Upgrade Opportunity */}
        {upgrade.show && (
          <>
            <Separator />
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold">
                    {t("Upgrade to")} {upgrade.nextTier}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t(upgrade.messageKey)}
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <Button size="sm" className="text-xs h-8">
                      {t("Upgrade to")} {upgrade.nextTier}
                      <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {tierDetails[upgrade.nextTier].quarterly === "Custom"
                        ? t("Custom")
                        : tierDetails[upgrade.nextTier].quarterly}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default SubscriptionCard;
